import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { typeLabel } from "../../constants";
import type { ImportTargetType } from "../../importPipeline";

export interface ImportItemRecord {
  id: string;
  file: File;
  fileName?: string;
  name: string;
  include: boolean;
  status: string;
  error?: string;
  preview?: any;
  asType?: ImportTargetType;
  payload?: any;
  /* attachment-file pool: real documents matched to corpus emails by filename */
  kind?: "attachmentFile";
  dataUrl?: string;
  /** payload as originally parsed, before any pool files were applied */
  basePayload?: any;
  /** how many email attachments this pool file is attached to */
  matches?: number;
}

export interface ImportItemProps {
  it: ImportItemRecord;
  target: ImportTargetType;
  onName: (id: string, name: string) => void;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onSwitch: (it: ImportItemRecord) => void;
}

interface TierEntry {
  cls: string;
  icon: string;
  label: string;
}
const TIER: Record<string, TierEntry> = {
  parsed: { cls: "t1", icon: "\u26A1", label: "Parsed" },
  mapped: { cls: "t2", icon: "\uD83D\uDD0D", label: "Mapped" },
  extracted: { cls: "t3", icon: "\uD83E\uDD16", label: "Extracted" },
};

/* One row in the file-drop import list — status pill, name editor,
   preview details, and the type-switcher hint if the file was detected as a
   different entity than the target tab. */
export function ImportItem({
  it, target, onName, onToggle, onRemove, onSwitch,
}: ImportItemProps): JSX.Element {
  const st = it.status;
  const tag = TIER[st];
  const pv = it.preview;
  if (it.kind === "attachmentFile") {
    const matched = (it.matches || 0) > 0;
    return (
      <div className={"impitem" + (st === "error" ? " err" : "")}>
        <div className="iihd">
          <input
            type="checkbox"
            className="chk"
            style={{ width: 16, height: 16 }}
            checked={!!it.include}
            disabled={st === "processing" || st === "error"}
            onChange={() => onToggle(it.id)}
          />
          <span
            className="mono"
            style={{
              flex: 1, minWidth: 0, fontSize: 12,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {it.fileName || it.file.name}
          </span>
          {st === "processing" && (
            <span className="tchip">
              <span className="spin" /> Reading…
            </span>
          )}
          {st === "error" && (
            <span className="tchip terr">
              <AlertTriangle size={11} /> Error
            </span>
          )}
          {st === "file" && (
            <span className="tchip tatt">{"\uD83D\uDCCE"} Attachment file</span>
          )}
          <button className="icon-btn" title="Remove" onClick={() => onRemove(it.id)}>
            <X size={14} />
          </button>
        </div>
        {st === "error" && <div className="imperr">{it.error}</div>}
        {st === "file" && (
          <div className="imppv">
            {matched ? (
              <>
                <span className="pk">
                  Attached to {it.matches} email attachment{it.matches === 1 ? "" : "s"}
                </span>{" "}
                · matched by filename
              </>
            ) : it.include ? (
              <span className="tert">
                {"\u26A0"} No email in this import references this filename yet.
              </span>
            ) : (
              <span className="tert">Excluded — not attached to any email.</span>
            )}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={"impitem" + (st === "error" ? " err" : "")}>
      <div className="iihd">
        <input
          type="checkbox"
          className="chk"
          style={{ width: 16, height: 16 }}
          checked={!!it.include}
          disabled={st === "processing" || st === "error"}
          onChange={() => onToggle(it.id)}
        />
        <input
          className="inp mono iinm"
          value={it.name}
          onChange={(e) => onName(it.id, e.target.value)}
          disabled={st === "processing"}
        />
        {st === "processing" && (
          <span className="tchip">
            <span className="spin" /> Working…
          </span>
        )}
        {st === "error" && (
          <span className="tchip terr">
            <AlertTriangle size={11} /> Error
          </span>
        )}
        {tag && (
          <span className={"tchip " + tag.cls}>
            {tag.icon} {tag.label}
          </span>
        )}
        <button className="icon-btn" title="Remove" onClick={() => onRemove(it.id)}>
          <X size={14} />
        </button>
      </div>
      {st === "error" && <div className="imperr">{it.error}</div>}
      {pv && pv.kind === "corpus" && (
        <div className="imppv">
          <span className="pk">
            {pv.count} email{pv.count === 1 ? "" : "s"}
          </span>{" "}
          · format {pv.format}
          {pv.first ? " · " + pv.first + (pv.last && pv.last !== pv.first ? " → " + pv.last : "") : ""}
          {pv.attCount
            ? " · " + pv.attCount + " attachment" + (pv.attCount === 1 ? "" : "s") +
              " (" + pv.attWithFile + " with files)"
            : ""}
          {pv.subjects && pv.subjects.length ? (
            <div className="tert" style={{ marginTop: 3 }}>
              {pv.subjects.slice(0, 3).map((s: string) => "\u201C" + s + "\u201D").join(" · ")}
            </div>
          ) : null}
          {(pv.warnings || []).map((w: string, i: number) => (
            <div key={i} className="tert" style={{ marginTop: 2 }}>
              {"\u26A0 " + w}
            </div>
          ))}
        </div>
      )}
      {pv && pv.kind === "eval" && (
        <div className="imppv">
          <span className="pk">
            {pv.labeled} labeled email{pv.labeled === 1 ? "" : "s"}
          </span>
          {pv.fields.length ? " · fields: " + pv.fields.join(", ") : ""}
        </div>
      )}
      {pv && pv.kind === "workstream" && (
        <div className="imppv">
          <span className="pk">
            {pv.wsCount} workstream{pv.wsCount === 1 ? "" : "s"}
          </span>{" "}
          · {pv.entities} entit{pv.entities === 1 ? "y" : "ies"} · {pv.people}{" "}
          {pv.people === 1 ? "person" : "people"}
          {pv.deals && pv.deals.length ? " · " + pv.deals.join(", ") : ""}
        </div>
      )}
      {it.asType && it.asType !== target && st !== "processing" && st !== "error" && (
        <div className="imphint">
          <AlertTriangle size={12} /> This looks like {typeLabel(it.asType)} data — importing as{" "}
          {typeLabel(it.asType)}.
          <button onClick={() => onSwitch(it)}>Use {typeLabel(target)} instead</button>
        </div>
      )}
    </div>
  );
}
